import React, { useEffect, useRef } from "react";
import {
  AttributeEditor,
  AttributeEditorProps,
  Button,
  Container,
  ExpandableSection,
  FormField,
  Header,
  Icon,
  SegmentedControl,
  Select,
  SelectProps,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import {
  OmsFilterOperator,
  OmsRecordFilter,
  OmsReport,
} from "../../lusca-search-api/generated-src";
import {
  OmsFilterCondition,
  OmsFilterCriteriaState,
} from "../../hooks/useOmsFilterCriteria";
import { columnValueError, comparisonLabel } from "./omsReportFormatting";
import OMSFilterValue from "./OMSFilterValue";
import { conditionOperators } from "./omsFilterEditing";

function filterFields(
  report: OmsReport,
  state: OmsFilterCriteriaState,
  registerField: (index: number, control: SelectProps.Ref | null) => void
): AttributeEditorProps.FieldDefinition<OmsFilterCondition>[] {
  return [
    {
      label: "Field",
      errorText: (criterion) =>
        criterion.column && !report.columns.includes(criterion.column)
          ? "This field is not in the selected source. Choose another field."
          : undefined,
      control: (criterion, index) => (
        <Select
          expandToViewport
          ref={(control) => {
            registerField(index, control);
          }}
          ariaLabel={`Field ${index + 1}`}
          filteringType="auto"
          filteringPlaceholder="Search report fields"
          filteringAriaLabel="Search report fields"
          filteringClearAriaLabel="Clear field search"
          noMatch="No matching fields in this report."
          placeholder="Choose a field"
          selectedOption={
            criterion.column
              ? { value: criterion.column, label: criterion.column }
              : null
          }
          options={report.columns.map((column) => ({
            value: column,
            label: column,
            description: {
              string: "Text",
              number: "Number",
              date: "Date",
              datetime: "Date and time",
            }[report.columnTypes?.[column] || "string"],
          }))}
          onChange={({ detail }) => {
            const column = detail.selectedOption.value || "";
            state.setColumn(criterion.id, column);
          }}
        />
      ),
    },
    {
      label: "Condition",
      errorText: (criterion) =>
        criterion.column &&
        report.columns.includes(criterion.column) &&
        !conditionOperators(report, criterion).includes(criterion.operator)
          ? "Choose a condition supported by this field."
          : undefined,
      control: (criterion, index) => {
        const type = report.columnTypes?.[criterion.column] || "string";
        const allowed = conditionOperators(report, criterion);
        return (
          <Select
            expandToViewport
            ariaLabel={`Condition ${index + 1}`}
            disabled={!criterion.column}
            selectedOption={{
              value: criterion.operator,
              label: comparisonLabel(criterion.operator, type),
            }}
            options={allowed.map((value) => ({
              value,
              label: comparisonLabel(value, type),
            }))}
            onChange={({ detail }) => {
              const operator = detail.selectedOption.value as OmsFilterOperator;
              state.setOperator(criterion.id, operator);
            }}
          />
        );
      },
    },
    {
      label: "Value",
      errorText: (criterion) =>
        criterion.valueNotice ||
        (criterion.column && (criterion.value || criterion.values?.length)
          ? columnValueError(
              report.columnTypes?.[criterion.column] || "string",
              criterion.operator,
              criterion.value,
              criterion.values
            )
          : undefined),
      constraintText: (criterion) => {
        if (
          !criterion.column ||
          ["isNull", "isNotNull"].includes(criterion.operator)
        )
          return undefined;
        const type = report.columnTypes?.[criterion.column] || "string";
        if (type === "date") return "Use YYYY-MM-DD.";
        if (type === "datetime") return "Use UTC or an offset such as +01:00.";
        if (["in", "notIn"].includes(criterion.operator)) return undefined;
        if (type === "string")
          return ["contains", "notContains"].includes(criterion.operator)
            ? "Text matching is case-sensitive."
            : "Case-sensitive. Leave blank for an empty value.";
        return undefined;
      },
      control: (criterion, index) => (
        <OMSFilterValue
          report={report}
          criterion={criterion}
          index={index}
          update={state.update}
        />
      ),
    },
  ];
}
export function AppliedOmsFilters({
  report,
  filters,
  operation,
  textCaseSensitive = false,
  compact = false,
}: {
  report: OmsReport;
  filters: OmsRecordFilter[];
  operation: "and" | "or";
  textCaseSensitive?: boolean;
  compact?: boolean;
}) {
  const matchingLabel =
    operation === "or" ? "Any condition (OR)" : "All conditions (AND)";
  const contents = (
    <ul className="oms-report-download__applied-filters">
      {filters.map((filter, index) => (
        // Accepted conditions are immutable and may repeat.
        // eslint-disable-next-line react/no-array-index-key
        <li key={`${index}:${filter.column}`}>
          <strong>{filter.column}</strong>{" "}
          {comparisonLabel(
            filter.operator,
            report.columnTypes?.[filter.column]
          ).toLowerCase()}
          {!textCaseSensitive &&
            ["contains", "notContains"].includes(filter.operator) &&
            " (case-insensitive)"}
          {filter.value !== undefined && (
            <>
              {" "}
              <span>{filter.value === "" ? '"" (blank)' : filter.value}</span>
            </>
          )}
          {filter.values && (
            <>
              {" "}
              <span>
                {filter.values
                  .map((value) =>
                    value === "" ? '"" (blank)' : JSON.stringify(value)
                  )
                  .join(", ")}
              </span>
            </>
          )}
        </li>
      ))}
    </ul>
  );
  return compact ? (
    <ExpandableSection
      variant="container"
      headingTagOverride="h3"
      headerText="Applied filters"
      headerCounter={`(${filters.length})`}
      headerDescription={filters.length > 1 ? matchingLabel : undefined}
    >
      {contents}
    </ExpandableSection>
  ) : (
    <Container
      header={
        <Header
          variant="h3"
          counter={`(${filters.length})`}
          description={filters.length > 1 ? matchingLabel : undefined}
        >
          Applied filters
        </Header>
      }
    >
      {contents}
    </Container>
  );
}
export default function OMSRecordFilters({
  report,
  state,
  focusRequested = false,
  onFocusHandled,
  embedded = false,
}: {
  report: OmsReport;
  state: OmsFilterCriteriaState;
  focusRequested?: boolean;
  onFocusHandled?: () => void;
  embedded?: boolean;
}) {
  const fieldRefs = useRef<Array<SelectProps.Ref | null>>([]);
  const pendingFocus = useRef<number | null>(null);
  useEffect(() => {
    if (!focusRequested) return;
    fieldRefs.current[0]?.focus();
    onFocusHandled?.();
  }, [focusRequested, onFocusHandled]);
  useEffect(() => {
    if (pendingFocus.current === null) return;
    fieldRefs.current[pendingFocus.current]?.focus();
    pendingFocus.current = null;
  }, [state.criteria]);
  const contents = (
    <SpaceBetween size="m">
      {state.criteria.length > 1 && (
        <div className="oms-report-download__matching">
          <FormField
            label="Include rows matching"
            constraintText={
              state.operation === "and"
                ? "Every condition must match."
                : "At least one condition must match."
            }
          >
            <SegmentedControl
              label="Include rows matching"
              selectedId={state.operation}
              options={[
                { id: "and", text: "All conditions (AND)" },
                { id: "or", text: "Any condition (OR)" },
              ]}
              onChange={({ detail }) =>
                state.setOperation(detail.selectedId as "and" | "or")
              }
            />
          </FormField>
        </div>
      )}
      <AttributeEditor
        items={state.criteria}
        definition={filterFields(report, state, (index, control) => {
          fieldRefs.current[index] = control;
        })}
        gridLayout={[
          { rows: [[1], [1], [1]], removeButton: { ownRow: true } },
          {
            breakpoint: "xs",
            rows: [[4, 3], [1]],
            removeButton: { ownRow: true },
          },
          {
            breakpoint: "s",
            rows: [[4, 3, 5]],
            removeButton: { width: "auto" },
          },
        ]}
        addButtonText="Add filter"
        removeButtonText="Remove"
        removeButtonAriaLabel={(item) =>
          `Remove filter ${state.criteria.indexOf(item) + 1}`
        }
        isItemRemovable={() => state.criteria.length > 1}
        disableAddButton={state.criteria.length >= 20}
        additionalActions={
          <Button
            onClick={() => {
              pendingFocus.current = 0;
              state.clear();
            }}
          >
            Clear all filters
          </Button>
        }
        additionalInfo={
          state.criteria.length >= 20
            ? "You can add up to 20 filters."
            : undefined
        }
        i18nStrings={{
          itemRemovedAriaLive: "Filter removed",
          errorIconAriaLabel: "Error",
        }}
        onAddButtonClick={() => {
          pendingFocus.current = state.criteria.length;
          state.add();
        }}
        onRemoveButtonClick={({ detail }) => {
          pendingFocus.current = Math.min(
            detail.itemIndex,
            state.criteria.length - 2
          );
          state.remove(detail.itemIndex);
        }}
      />
    </SpaceBetween>
  );
  if (embedded) return contents;
  return (
    <Container
      header={
        <Header variant="h3" description="Choose which rows to include.">
          <span className="oms-report-download__icon-label">
            <span aria-hidden="true">
              <Icon name="filter" variant="subtle" />
            </span>
            <span>Report filters</span>
          </span>
        </Header>
      }
    >
      {contents}
    </Container>
  );
}
